import type { TelemetryState } from '@ctw/shared/context/telemetry/telemetry-boundary';

// Every Zus API environment serves auth-service through a `<service>.zusapi.com` subdomain (see
// `getZusApiBaseUrl`/`getApiServiceBaseUrl` in `packages/shared/utils/environments.ts`).
const ZUS_API_HOST_SUFFIX = '.zusapi.com';

export interface PaginationPage<T> {
  items: ReadonlyArray<T>;
  nextUrl?: string;
}

export interface FollowPaginationLinksOptions<T> {
  initialUrl: string;
  fetchPage: (url: string, signal: AbortSignal | undefined) => Promise<PaginationPage<T>>;
  signal?: AbortSignal;
  telemetry: Pick<TelemetryState, 'trackError'>;
  operationId: string;
}

// Allows a Zus API host or the page's own origin; rejects everything else.
const isAllowedPaginationHost = (url: URL): boolean => {
  if (url.hostname.endsWith(ZUS_API_HOST_SUFFIX)) {
    return true;
  }

  return typeof window !== 'undefined' && url.origin === window.location.origin;
};

// Follows a JSON:API `links.next` chain one page at a time. Stops when: there is no next link;
// the abort signal fires; the next link normalizes to a URL already fetched in this chain (a
// cycle, of which a server re-emitting the URL just fetched is the shortest case); or the next
// link's host is not allowed. Every stop returns whatever was accumulated so far rather than
// failing the whole call; the cycle and disallowed-host cases also report a telemetry error, the
// abort case does not.
export async function followPaginationLinks<T>({
  initialUrl,
  fetchPage,
  signal,
  telemetry,
  operationId,
}: FollowPaginationLinksOptions<T>): Promise<Array<T>> {
  const items: Array<T> = [];
  const fetchedUrls = new Set<string>();
  let currentUrl: URL | undefined = new URL(initialUrl);
  let pageCount = 0;

  while (currentUrl) {
    fetchedUrls.add(currentUrl.href);
    const page = await fetchPage(currentUrl.href, signal);
    items.push(...page.items);
    pageCount += 1;

    if (signal?.aborted) {
      return items;
    }

    const nextUrl: URL | undefined = page.nextUrl ? new URL(page.nextUrl, currentUrl) : undefined;

    if (!nextUrl) {
      currentUrl = undefined;
      continue;
    }

    if (fetchedUrls.has(nextUrl.href)) {
      telemetry.trackError({
        message: `Stopped following pagination for ${operationId} after a repeated link formed a cycle`,
        error: new Error('Pagination link cycle detected'),
        context: { operationId, pageCount, nextUrlOrigin: nextUrl.origin },
      });
      currentUrl = undefined;
      continue;
    }

    if (!isAllowedPaginationHost(nextUrl)) {
      telemetry.trackError({
        message: `Refused to follow pagination link for ${operationId} to a disallowed host`,
        error: new Error('Pagination link host not allowed'),
        context: { operationId, pageCount, nextUrlOrigin: nextUrl.origin },
      });
      currentUrl = undefined;
      continue;
    }

    currentUrl = nextUrl;
  }

  return items;
}
